<template>
  <v-dialog v-model="dialog" persistent :width="964">
    <AlFormCard :title="title" :subtitle="subtitle">
      <template #error>
        <ErrorAlert
          v-if="error != null"
          :error="error"
          @clearErrors="clearErrors()"
          class="mb-0 rounded"
        />
      </template>

      <template #appendAction>
        <v-btn
          variant="tonal"
          class="rounded"
          size="32"
          @click="closeDialog()"
          icon
          color="default"
          density="comfortable"
        >
          <v-icon size="18" icon="mdi-close" />
        </v-btn>
      </template>

      <div class="d-flex flex-column">
        <v-stepper v-model="wizardStep" elevation="0" v-if="!props.isEdit">
          <v-stepper-header class="elevation-0">
            <v-stepper-item
              title="Profile"
              :value="WIZARD_STEP.Profile"
              :complete="wizardStep > WIZARD_STEP.Profile"
              :color="wizardStep > WIZARD_STEP.Profile ? 'success' : ''"
              class="py-5 pl-0"
              :class="wizardStep > WIZARD_STEP.Profile ? 'text-success' : ''"
              :style="wizardStep > WIZARD_STEP.Profile ? 'opacity: 1;' : ''"
            ></v-stepper-item>

            <v-divider></v-divider>

            <v-stepper-item
              class="py-5 pr-0"
              title="Site Permission"
              :value="WIZARD_STEP.Site"
            ></v-stepper-item>
          </v-stepper-header>
        </v-stepper>

        <v-window class="mx-0 mb-4" v-model="wizardStep">
          <v-window-item :value="WIZARD_STEP.Profile">
            <v-form ref="form" v-model="isProfileFormValid" validate-on="submit lazy">
              <h2 class="text-subtitle-2 font-weight-medium my-3">User Details</h2>
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="customerData.first_name"
                    label="First Name*"
                    variant="outlined"
                    hide-details="auto"
                    density="compact"
                    @update:model-value="clearErrors()"
                    :rules="[requiredValidator]"
                    :error-messages="error && (error.fieldErrors['first_name'] as string[])"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="customerData.last_name"
                    label="Last Name*"
                    variant="outlined"
                    hide-details="auto"
                    density="compact"
                    @update:model-value="clearErrors()"
                    :rules="[requiredValidator]"
                    :error-messages="error && (error.fieldErrors['last_name'] as string[])"
                  ></v-text-field>
                </v-col>

                <v-col cols="12">
                  <v-text-field
                    v-model="customerData.email"
                    label="Email*"
                    variant="outlined"
                    hide-details="auto"
                    placeholder="jdoe@email.com"
                    density="compact"
                    :disabled="isEdit"
                    @update:model-value="clearErrors()"
                    :rules="[requiredValidator, emailValidator]"
                    :error-messages="error && (error.fieldErrors['email'] as string[])"
                    validate-on="input"
                  ></v-text-field>
                </v-col>
              </v-row>
              <h2 class="text-subtitle-2 font-weight-medium my-3 pt-3">Customer Profile</h2>
              <v-row>
                <v-col cols="12">
                  <v-text-field
                    v-model="customerData.profile!.title"
                    label="Title*"
                    variant="outlined"
                    hide-details="auto"
                    density="compact"
                    @update:model-value="clearErrors()"
                    :rules="[requiredValidator]"
                    :error-messages="
                      error &&
                      error.fieldErrors['profile'] &&
                      ((error.fieldErrors['profile'] as IFieldErrors)['title'] as string[])
                    "
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-form>
          </v-window-item>

          <v-window-item :value="WIZARD_STEP.Site">
            <CustomerDetailSitesForm
              v-if="customerId"
              :customer-id="customerId"
              ref="customerDetailSitesFormRef"
              @save="closeDialog()"
            />
          </v-window-item>
        </v-window>
      </div>

      <template #actions>
        <v-btn
          v-if="wizardStep == WIZARD_STEP.Site"
          class="mr-2"
          variant="outlined"
          :disabled="isCustomerSitesPending"
          @click="wizardStep = WIZARD_STEP.Profile"
          >Back</v-btn
        >
        <v-spacer></v-spacer>

        <v-btn
          v-if="wizardStep == WIZARD_STEP.Profile"
          color="primary"
          variant="flat"
          :loading="loading"
          @click="saveCustomer()"
          >{{ props.isEdit ? 'Save' : 'Next' }}</v-btn
        >
        <v-btn
          v-else
          color="primary"
          variant="flat"
          :loading="isCustomerSitesPending"
          @click="saveCustomerSites()"
          >Save</v-btn
        >
      </template>
    </AlFormCard>
  </v-dialog>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { cloneDeep } from 'lodash'

import type { ISystemError, IFieldErrors } from '@/models/error'
import type { VForm } from 'vuetify/components'
import { Customer, type ICustomer, type ICustomerData } from '@/models/customer'

import { diffDeep } from '@/utils/helpers'
import { emailValidator, requiredValidator } from '@/utils/validators'
import { useCreateCustomer, useUpdateCustomer } from '@/composables/customer'

import AlFormCard from '../common/AlFormCard.vue'
import CustomerDetailSitesForm from './CustomerDetailSitesForm.vue'
import ErrorAlert from '../common/ErrorAlert.vue'

interface Props {
  customer?: ICustomer
  isEdit: boolean
}

const props = defineProps<Props>()

const customerData = ref<ICustomerData>({
  profile: {}
})

const originalCustomerData = ref<ICustomerData>({
  profile: {}
})

const dialog = defineModel<boolean>('dialog', { required: true, default: false })

watch(dialog, (value) => {
  if (value) {
    clearErrors()
    wizardStep.value = WIZARD_STEP.Profile
    customerData.value = !props.isEdit
      ? ({
          profile: {}
        } as ICustomerData)
      : cloneDeep(props.customer!)
  }
})

const form = ref<VForm>()
const isProfileFormValid = ref(false)

enum WIZARD_STEP {
  Profile = 1,
  Site = 2
}

const wizardStep = ref(WIZARD_STEP.Profile)

const title = computed(() =>
  wizardStep.value == WIZARD_STEP.Profile ? 'Customer Detail' : 'Customer Site Access'
)
const subtitle = computed(() =>
  wizardStep.value == WIZARD_STEP.Profile
    ? `${isEditing.value ? 'Update' : 'Create'} customer information`
    : 'Give your customer access to view specific TAG sites'
)

function closeDialog() {
  clearErrors()
  dialog.value = false
}

const {
  isPending: isUpdatePending,
  error: updateError,
  mutate: updateMutate,
  reset: updateReset
} = useUpdateCustomer()
const {
  isPending: isCreatePending,
  error: createError,
  mutate: createMutate,
  reset: createReset
} = useCreateCustomer()

const loading = computed(() => (isEditing.value ? isUpdatePending.value : isCreatePending.value))
const localError = ref<ISystemError | null>(null)
const error = computed({
  get: () => {
    return localError.value || isEditing.value
      ? updateError.value
      : createError.value || customerDetailSitesFormRef.value?.error
  },
  set: (value) => {
    localError.value = value
  }
})

function clearErrors() {
  isEditing.value ? updateReset() : createReset()
  customerDetailSitesFormRef.value?.clearErrors()
  error.value = null
}

const customerId = computed(() => customerData.value.id)

const isEditing = computed(() => props.isEdit || !!customerId.value)

async function saveCustomer() {
  clearErrors()
  const { valid } = await form.value!.validate()

  if (valid) {
    let customerInstance: ICustomer | null = null

    try {
      // make username same as email
      // this is a workaround for the backend to accept the email as username
      customerData.value.username = customerData.value.email
      customerInstance = new Customer(customerData.value)
    } catch (e: any) {
      error.value = e
    }

    if (customerInstance != null) {
      const saveAction = isEditing.value ? updateMutate : createMutate

      let payload = customerInstance

      if (isEditing.value) {
        payload = diffDeep(
          customerInstance,
          props.isEdit ? props.customer : new Customer(originalCustomerData.value),
          true
        )
      }

      saveAction(payload, {
        onSuccess: (data) => {
          // set customerData with newly saved data from api

          customerData.value = cloneDeep(data)
          originalCustomerData.value = cloneDeep(data)

          if (props.isEdit) {
            closeDialog()
          } else {
            wizardStep.value = WIZARD_STEP.Site
          }
        }
      })
    }
  }
}

const customerDetailSitesFormRef = ref<typeof CustomerDetailSitesForm>()

const isCustomerSitesPending = computed<boolean>(() => customerDetailSitesFormRef.value?.isPending)

function saveCustomerSites() {
  return customerDetailSitesFormRef.value?.saveCustomerSites()
}
</script>
